import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Loading from '../../components/Loading';
import Modal from '../../components/Modal';
import Textarea from '../../components/Textarea';
import { useToast } from '../../context/ToastContext';
import { formatDateIST } from '../../utils/dateFormatter';
import {
  getOrganizerReviewerRegistrations,
  approveReviewerRegistration,
  rejectReviewerRegistration,
  getConferenceDetailsOrganizer,
  bulkApproveReviewerRegistrations,
  bulkRejectReviewerRegistrations,
} from '../../utils/api';

const STATUS_TABS = [
  { key: 'PENDING_APPROVAL', label: 'Pending', icon: '⏳' },
  { key: 'APPROVED', label: 'Approved', icon: '✓' },
  { key: 'REJECTED', label: 'Rejected', icon: '✗' },
  { key: 'ALL', label: 'All', icon: '📋' },
];

const ReviewerRequests = () => {
  const { conferenceId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [conference, setConference] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({ PENDING_APPROVAL: 0, APPROVED: 0, REJECTED: 0, ALL: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('PENDING_APPROVAL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [processingId, setProcessingId] = useState(null);

  // Reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReviewerReason, setRejectReviewerReason] = useState('');

  // Approve confirmation
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);

  const [selectedRegs, setSelectedRegs] = useState([]);
  const [isBulkAction, setIsBulkAction] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [confRes, regsRes] = await Promise.all([
        getConferenceDetailsOrganizer(conferenceId),
        getOrganizerReviewerRegistrations(conferenceId, {
          status: activeTab,
          search: search || undefined,
          page,
          limit: 25,
        }),
      ]);
      setConference(confRes.data || confRes);
      setRegistrations(regsRes.data?.registrations || []);
      setStats(regsRes.data?.stats || { PENDING_APPROVAL: 0, APPROVED: 0, REJECTED: 0, ALL: 0 });
      setTotalPages(regsRes.data?.pages || 1);
    } catch (error) {
      console.error('Error fetching reviewer requests:', error);
      toast.error(error.response?.data?.message || 'Failed to load reviewer requests');
    } finally {
      setLoading(false);
    }
  }, [conferenceId, activeTab, search, page, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, search]);

  const handleApproveConfirm = async () => {
    if (isBulkAction) {
      try {
        setProcessingId('bulk');
        await bulkApproveReviewerRegistrations(conferenceId, selectedRegs, '');
        toast.success(`${selectedRegs.length} reviewer(s) approved successfully`);
        setShowApproveConfirm(false);
        setSelectedRegs([]);
        await fetchData();
      } catch (error) {
        console.error('Error bulk approving reviewers:', error);
        toast.error(error.response?.data?.message || 'Failed to approve reviewers');
      } finally {
        setProcessingId(null);
      }
    } else if (approveTarget) {
      try {
        setProcessingId(approveTarget._id);
        await approveReviewerRegistration(conferenceId, approveTarget._id);
        toast.success('Reviewer approved successfully');
        setShowApproveConfirm(false);
        setApproveTarget(null);
        await fetchData();
      } catch (error) {
        console.error('Error approving reviewer:', error);
        toast.error(error.response?.data?.message || 'Failed to approve reviewer');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const handleRejectConfirm = async () => {
    if (isBulkAction) {
      try {
        setProcessingId('bulk');
        await bulkRejectReviewerRegistrations(conferenceId, selectedRegs, rejectReason, rejectReviewerReason);
        toast.success(`${selectedRegs.length} reviewer(s) rejected`);
        setShowRejectModal(false);
        setSelectedRegs([]);
        setRejectReason('');
        setRejectReviewerReason('');
        await fetchData();
      } catch (error) {
        console.error('Error bulk rejecting reviewers:', error);
        toast.error(error.response?.data?.message || 'Failed to reject reviewers');
      } finally {
        setProcessingId(null);
      }
    } else if (rejectTarget) {
      try {
        setProcessingId(rejectTarget._id);
        await rejectReviewerRegistration(conferenceId, rejectTarget._id, {
          reason: rejectReason,
          reviewerReason: rejectReviewerReason,
        });
        toast.success('Reviewer rejected');
        setShowRejectModal(false);
        setRejectTarget(null);
        setRejectReason('');
        setRejectReviewerReason('');
        await fetchData();
      } catch (error) {
        console.error('Error rejecting reviewer:', error);
        toast.error(error.response?.data?.message || 'Failed to reject reviewer');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const openRejectModal = (reg) => {
    setIsBulkAction(false);
    setRejectTarget(reg);
    setRejectReason('');
    setRejectReviewerReason('');
    setShowRejectModal(true);
  };

  const openApproveConfirm = (reg) => {
    setIsBulkAction(false);
    setApproveTarget(reg);
    setShowApproveConfirm(true);
  };

  const openBulkApproveConfirm = () => {
    setIsBulkAction(true);
    setApproveTarget(null);
    setShowApproveConfirm(true);
  };

  const openBulkRejectConfirm = () => {
    setIsBulkAction(true);
    setRejectTarget(null);
    setRejectReason('');
    setRejectReviewerReason('');
    setShowRejectModal(true);
  };

  const formatDate = (date) =>
    formatDateIST(date);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING_APPROVAL':
        return <Badge variant="warning">⏳ Pending</Badge>;
      case 'APPROVED':
        return <Badge variant="success">✓ Approved</Badge>;
      case 'REJECTED':
        return <Badge variant="danger">✗ Rejected</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  if (loading && !conference) {
    return (
      <>
        <Navbar />
        <Loading fullScreen message="Loading reviewer requests..." />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/organizer/manage-conference/${conferenceId}`)}
          >
            ← Back to Conference
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reviewer Requests</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              {conference?.name || 'Conference'} — Manage reviewer applications
            </p>
          </div>
          {stats.PENDING_APPROVAL > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
              </span>
              <span className="text-sm font-semibold text-amber-800">
                {stats.PENDING_APPROVAL} pending review{stats.PENDING_APPROVAL !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.key
                    ? 'bg-blue-500 text-white'
                    : tab.key === 'PENDING_APPROVAL' && (stats[tab.key] || 0) > 0
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {stats[tab.key] || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <Card className="mb-6">
          <Input
            label="Search Reviewers"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or affiliation..."
          />
        </Card>

        {/* Bulk Actions Bar */}
        {registrations.length > 0 && (
          <div className="mb-6 flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg animate-fade-in">
            <button
              onClick={() => {
                const allSelected = registrations.every(reg => selectedRegs.includes(reg._id));
                if (allSelected) {
                  setSelectedRegs([]);
                } else {
                  setSelectedRegs(registrations.map(reg => reg._id));
                }
              }}
              className="text-blue-600 hover:underline text-sm font-semibold"
            >
              {registrations.every(reg => selectedRegs.includes(reg._id))
                ? 'Deselect All'
                : 'Select All Visible'}
            </button>
            {selectedRegs.length > 0 && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-700 font-medium">
                  {selectedRegs.length} reviewer(s) selected
                </span>

                {registrations.some(reg => selectedRegs.includes(reg._id) && ['PENDING_APPROVAL', 'REJECTED'].includes(reg.approvalStatus)) && (
                  <Button
                    size="sm"
                    variant="success"
                    onClick={openBulkApproveConfirm}
                    disabled={processingId !== null}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    ✓ Approve Selected
                  </Button>
                )}

                {registrations.some(reg => selectedRegs.includes(reg._id) && ['PENDING_APPROVAL', 'APPROVED'].includes(reg.approvalStatus)) && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={openBulkRejectConfirm}
                    disabled={processingId !== null}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    ✗ Reject Selected
                  </Button>
                )}

                <button
                  onClick={() => setSelectedRegs([])}
                  className="text-gray-500 hover:underline text-sm"
                >
                  Clear Selection
                </button>
              </>
            )}
          </div>
        )}

        {/* Registrations List */}
        {loading ? (
          <Loading message="Loading..." />
        ) : registrations.length === 0 ? (
          <Card className="text-center py-12">
            <div className="text-6xl mb-4">
              {activeTab === 'PENDING_APPROVAL' ? '✅' : '📋'}
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {activeTab === 'PENDING_APPROVAL'
                ? 'No Pending Requests'
                : activeTab === 'ALL'
                ? 'No Reviewer Registrations'
                : `No ${activeTab.replace('_', ' ').toLowerCase()} reviewers`}
            </h3>
            <p className="text-gray-600">
              {activeTab === 'PENDING_APPROVAL'
                ? 'All reviewer applications have been processed.'
                : 'No reviewer registrations match your filters.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {registrations.map((reg) => {
              const reviewer = reg.reviewerId;
              const isProcessing = processingId === reg._id;

              return (
                <Card key={reg._id} className="animate-fade-in">
                  <div className="flex items-start gap-4">
                    {/* Checkbox for Bulk Actions */}
                    <input
                      type="checkbox"
                      checked={selectedRegs.includes(reg._id)}
                      onChange={(e) => {
                        const id = reg._id;
                        setSelectedRegs(prev =>
                          prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
                        );
                      }}
                      className="mt-3.5 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                    />
                    <div className="flex-1 flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Reviewer Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {(reviewer?.name || '?')[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {reviewer?.name || 'Unknown Reviewer'}
                          </h3>
                          <p className="text-sm text-gray-500 truncate">{reviewer?.email || ''}</p>
                        </div>
                        {getStatusBadge(reg.approvalStatus)}
                      </div>

                      {/* Details Row */}
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 ml-[52px]">
                        {reviewer?.affiliation && (
                          <span className="flex items-center gap-1">
                            <span className="text-gray-400">🏛</span>
                            {reviewer.affiliation}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <span className="text-gray-400">📅</span>
                          Applied: {formatDate(reg.registeredAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="text-gray-400">📝</span>
                          {reg.currentAssignments || 0} assignments
                        </span>
                      </div>

                      {/* Expertise Domains */}
                      {reviewer?.expertiseDomains?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2 ml-[52px]">
                          {reviewer.expertiseDomains.slice(0, 5).map((domain, idx) => (
                            <Badge key={idx} variant="info" size="sm" className="text-xs">
                              {domain}
                            </Badge>
                          ))}
                          {reviewer.expertiseDomains.length > 5 && (
                            <Badge variant="default" size="sm" className="text-xs">
                              +{reviewer.expertiseDomains.length - 5} more
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Decision info for non-pending */}
                      {reg.approvalDecision?.decidedAt && reg.approvalStatus !== 'PENDING_APPROVAL' && (
                        <div className="mt-2 ml-[52px] text-xs text-gray-500">
                          Decision by {reg.approvalDecision.decidedBy?.name || 'Organizer'} on{' '}
                          {formatDate(reg.approvalDecision.decidedAt)}
                          {reg.approvalDecision.reason && (
                            <span className="ml-2 text-gray-400">
                              — {reg.approvalDecision.reason}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0 lg:flex-col lg:w-32">
                      {reg.approvalStatus === 'PENDING_APPROVAL' && (
                        <>
                          <Button
                            size="sm"
                            variant="primary"
                            fullWidth
                            disabled={isProcessing}
                            onClick={() => openApproveConfirm(reg)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {isProcessing ? '...' : '✓ Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            fullWidth
                            disabled={isProcessing}
                            onClick={() => openRejectModal(reg)}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            {isProcessing ? '...' : '✗ Reject'}
                          </Button>
                        </>
                      )}
                      {reg.approvalStatus === 'REJECTED' && (
                        <Button
                          size="sm"
                          variant="primary"
                          fullWidth
                          disabled={isProcessing}
                          onClick={() => openApproveConfirm(reg)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {isProcessing ? '...' : '✓ Approve'}
                        </Button>
                      )}
                      {reg.approvalStatus === 'APPROVED' && (
                        <Button
                          size="sm"
                          variant="outline"
                          fullWidth
                          disabled={isProcessing}
                          onClick={() => openRejectModal(reg)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          {isProcessing ? '...' : 'Revoke'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Approve Confirmation Modal */}
      <Modal
        isOpen={showApproveConfirm}
        onClose={() => {
          setShowApproveConfirm(false);
          setApproveTarget(null);
        }}
        title="Approve Reviewer(s)"
        size="sm"
      >
        {(approveTarget || isBulkAction) && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                {isBulkAction ? (
                  <>
                    You are about to approve <strong>{selectedRegs.length} reviewer(s)</strong> for this conference.
                  </>
                ) : (
                  <>
                    You are about to approve <strong>{approveTarget?.reviewerId?.name}</strong> as a reviewer for this conference.
                  </>
                )}
              </p>
              <p className="text-xs text-green-600 mt-2">
                They will gain access to conference submissions, bidding, and review functionality.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                fullWidth
                onClick={handleApproveConfirm}
                disabled={processingId !== null}
                className="bg-green-600 hover:bg-green-700"
              >
                {processingId !== null ? 'Approving...' : 'Confirm Approval'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowApproveConfirm(false);
                  setApproveTarget(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectTarget(null);
        }}
        title="Reject/Revoke Reviewer Application(s)"
        size="md"
      >
        {(rejectTarget || isBulkAction) && (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                {isBulkAction ? (
                  <>
                    You are about to reject/revoke <strong>{selectedRegs.length} reviewer application(s)</strong>.
                  </>
                ) : (
                  <>
                    You are about to reject <strong>{rejectTarget?.reviewerId?.name}</strong>'s reviewer application.
                  </>
                )}
              </p>
              <p className="text-xs text-red-600 mt-1">
                They will not be able to access conference submissions or place bids.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (visible to reviewer) <span className="text-gray-400">— optional</span>
              </label>
              <Textarea
                value={rejectReviewerReason}
                onChange={(e) => setRejectReviewerReason(e.target.value)}
                placeholder="e.g., Expertise does not match conference topics..."
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Internal Note <span className="text-gray-400">— optional, organizer-only</span>
              </label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Internal note for organizers..."
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                fullWidth
                onClick={handleRejectConfirm}
                disabled={processingId !== null}
                className="bg-red-600 hover:bg-red-700"
              >
                {processingId !== null ? 'Confirming...' : 'Confirm'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectTarget(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ReviewerRequests;
